import { Component, ElementRef, EventEmitter, forwardRef, Input, Output, ViewChild } from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { noop } from 'rxjs';
import { MatAutocomplete, MatAutocompleteOrigin } from '@angular/material/autocomplete';
import { generateId } from '../../../../_core/utils/generate-id';
import { FocusableControlValueAccessor } from '../FocusableControlValueAccessor';

@Component({
  selector: 'opac-text-input',
  templateUrl: './text-input.component.html',
  styleUrls: ['./text-input.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TextInputComponent),
      multi: true,
    }
  ]
})
export class TextInputComponent implements FocusableControlValueAccessor {
  readonly errorId = Math.random().toString(16).substring(2);
  readonly infinity = Infinity;

  @ViewChild('inputEl') inputEl!: ElementRef<HTMLInputElement>;

  /**
   * Sets the aria autocomplete attribute. Used in `<opac-select>`
   */
  @Input() ariaAutocomplete: string | undefined = undefined;
  /**
   * Sets the aria active descendant attribute. Used in `<opac-select>`
   */
  @Input() ariaActiveDescendant: string | undefined = undefined;
  /**
   * Sets the aria controls attribute. Used in `<opac-select>`
   */
  @Input() ariaControls: string | undefined = undefined;
  /**
   * Sets the aria has popup attribute. Used in `<opac-select>`
   */
  @Input() ariaHasPopup: string | undefined = undefined;
  /**
   * Sets the aria expanded attribute. Used in `<opac-select>`
   */
  @Input() ariaExpanded: boolean | undefined = undefined;
  /**
   * Determines input autocomplete attribute.
   */
  @Input() autocomplete: string = 'off';
  /**
   * Sets autofocus attribute in native input
   */
  @Input() autofocus: boolean = false;
  /**
   * Determines if counter should be visible.
   * It also needs to set max length to work.
   */
  @Input() counter: boolean = false;
  /**
   * Sets input disabled state
   */
  @Input() set disabled(value: boolean) {
    this.setDisabledState(value);
  };
  /**
   * Determines what icon should be visible at the end of input
   */
  @Input() icon?: string = undefined;
  /**
   * Input id for label join.
   * Defaults to random string.
   */
  @Input() id: string = generateId();
  /**
   * Determines if input is invalid.
   */
  @Input() invalid: boolean = false;

  @Input() matAutocomplete?: MatAutocomplete;
  @Input() matAutocompleteConnectedTo?: MatAutocompleteOrigin;
  /**
   * Determines input number max value.
   */
  @Input() max: number = this.infinity;
  /**
   * Determines input number min value.
   */
  @Input() min: number = -this.infinity;
  /**
   * Determines input min length attribute.
   */
  @Input() minLength: number = 0;
  /**
   * Determines input max length attribute.
   * Needed to show the counter.
   */
  @Input() maxLength: number = this.infinity;
  /**
   * Determines input name attribute.
   */
  @Input() name: string = '';
  /**
   * Determines input readonly status.
   */
  @Input() readonly: boolean = false;
  /**
   * Sets required state for input.
   * This prop won't validate, it is just for accessibility reasons.
   */
  @Input() required: boolean = false;
  /**
   * Sets role attribute for accessibility.
   */
  @Input() role: string | undefined = undefined;
  /**
   * Determines input type attribute.
   */
  @Input() type: string = 'text';
  /**
   * Emits when icon is clicked.
   */
  @Output() iconClick = new EventEmitter<MouseEvent>();
  @Output() blur = new EventEmitter<FocusEvent>();

  onChange: (value: string) => void = noop;
  onTouch: () => void = noop;

  isDisabled = false;
  value = '';

  handleOnTouch(event: FocusEvent) {
    this.onTouch();
    this.blur.emit(event);
  }

  handleIconClick(event: MouseEvent) {
    this.iconClick.emit(event);
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouch = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
  }

  focus(): void {
    this.inputEl.nativeElement.focus();
  }

  writeValue(obj: string): void {
    this.value = obj;
  }
}
